import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageComponent, WebsiteService, SupportedLanguage } from '../../../../../services/website.service';
import { Firestore, collection, query, where, getDocs } from '@angular/fire/firestore';
import { Subject } from 'rxjs';

export interface NewsPost {
  id: string;
  title: string;
  content: string;
  language: SupportedLanguage;
  createdAt: Date;
  status: 'draft' | 'published';
  categoryIds?: string[];
  domain?: string;
  domains?: string[];
  featuredImage?: string;
  slug?: string;
}

export interface NewsCategory {
  id: string;
  name: string;
  domain?: string;
}

@Component({
  selector: 'app-news-component',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './news-component.component.html',
  styleUrls: ['./news-component.component.scss']
})
export class NewsComponentComponent implements OnInit, OnDestroy, OnChanges {
  @Input() component!: PageComponent;
  @Input() mode: 'preview' | 'edit' = 'preview';
  @Input() isSelected: boolean = false;
  @Input() currentLocale: SupportedLanguage = 'nl';
  /** When set (e.g. from page editor), use this domain instead of global current domain to load posts. */
  @Input() domainOverride?: string;
  @Output() styleChange = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  posts: NewsPost[] = [];
  categories: NewsCategory[] = [];
  loading = false;
  categoriesLoading = false;

  private allCategories: { id: string; slug?: string; name: string }[] = [];

  constructor(
    private firestore: Firestore,
    private websiteService: WebsiteService
  ) {}

  ngOnInit() {
    this.loadCategories();
    this.loadPosts();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['domainOverride'] && this.getEffectiveDomain()) {
      this.loadCategories();
      this.loadPosts();
    }
    if (changes['component']) {
      const currentCategoryIds = this.getSelectedCategoryIds();
      const previousCategoryIds = changes['component'].previousValue?.data?.['selectedCategoryIds'] || [];

      if (JSON.stringify(currentCategoryIds) !== JSON.stringify(previousCategoryIds)) {
        this.loadPosts();
      }
    }
  }

  private getEffectiveDomain(): string {
    return (this.domainOverride && this.domainOverride.trim()) || this.websiteService.getCurrentDomain() || '';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadCategories() {
    this.categoriesLoading = true;
    try {
      const currentDomain = this.getEffectiveDomain();
      if (!currentDomain) {
        this.categories = [];
        this.categoriesLoading = false;
        return;
      }

      const categoriesRef = collection(this.firestore, 'categories');
      const q = query(
        categoriesRef,
        where('domain', '==', currentDomain)
      );

      const snapshot = await getDocs(q);
      this.categories = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data()['name'] || 'Unnamed',
        domain: doc.data()['domain']
      }));
      this.allCategories = snapshot.docs.map(doc => ({
        id: doc.id,
        slug: doc.data()['slug'] || '',
        name: doc.data()['name'] || ''
      }));

      this.categoriesLoading = false;
    } catch (error) {
      console.error('Error loading categories:', error);
      this.categoriesLoading = false;
    }
  }

  async loadPosts() {
    this.loading = true;
    try {
      const currentDomain = this.getEffectiveDomain();
      if (!currentDomain) {
        this.posts = [];
        this.loading = false;
        return;
      }

      const postsRef = collection(this.firestore, 'posts');
      const selectedIds = this.getSelectedCategoryIds();
      const postLimit = this.getLimit();
      const lang = this.currentLocale;

      const toPost = (d: { id: string; data: () => Record<string, unknown> }): NewsPost => {
        const data = d.data();
        const catIds = Array.isArray(data['categoryIds']) && (data['categoryIds'] as unknown[]).length > 0
          ? (data['categoryIds'] as string[]).filter((x): x is string => typeof x === 'string')
          : [];
        const doms = Array.isArray(data['domains']) ? (data['domains'] as string[]) : [];
        return {
          id: d.id,
          title: String(data['title'] ?? ''),
          content: String(data['content'] ?? ''),
          language: (data['language'] as SupportedLanguage) || lang,
          createdAt: (data['createdAt'] as { toDate?: () => Date })?.toDate?.() || new Date(),
          status: ((data['status'] as string) || 'draft') as 'draft' | 'published',
          categoryIds: catIds,
          domain: doms[0] ?? (data['domain'] as string),
          domains: doms,
          featuredImage: data['featuredImage'] as string | undefined,
          slug: data['slug'] as string | undefined
        };
      };

      // Simpele queries: alleen domain + language (2 equality filters, geen composite index nodig)
      // Status en categorie filteren we client-side
      const [snapDomain, snapDomains] = await Promise.all([
        getDocs(query(
          postsRef,
          where('domain', '==', currentDomain),
          where('language', '==', lang)
        )),
        getDocs(query(
          postsRef,
          where('domains', 'array-contains', currentDomain),
          where('language', '==', lang)
        ))
      ]);

      // Merge en deduplicate
      const byId = new Map<string, NewsPost>();
      [...snapDomain.docs, ...snapDomains.docs].forEach(d => {
        if (!byId.has(d.id)) {
          byId.set(d.id, toPost(d as { id: string; data: () => Record<string, unknown> }));
        }
      });

      // Client-side filtering: status = published
      let allPosts = Array.from(byId.values()).filter(p => p.status === 'published');

      // Client-side filtering: categorie
      if (selectedIds.length > 0) {
        allPosts = allPosts.filter(p =>
          p.categoryIds && p.categoryIds.some(catId => selectedIds.includes(catId))
        );
      }

      // Client-side sortering: nieuwste eerst
      allPosts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      // Limit
      this.posts = postLimit > 0 ? allPosts.slice(0, postLimit) : allPosts;

      this.loading = false;
    } catch (error) {
      console.error('Error loading posts:', error);
      this.loading = false;
    }
  }

  // Getters
  getLayoutType(): string {
    // 'grid' | 'list' | 'cards' | 'compact' | 'featured'
    return this.component.data['layoutType'] || 'cards';
  }

  getColumns(): number {
    return this.component.data['columns'] || 3;
  }

  getLimit(): number {
    return this.component.data['limit'] || 6;
  }

  getSelectedCategoryIds(): string[] {
    return this.component.data['selectedCategoryIds'] || [];
  }

  getHeading(): string {
    return this.component.data['heading'] || '';
  }

  getShowDate(): boolean {
    return this.component.data['showDate'] !== false;
  }

  getShowImage(): boolean {
    return this.component.data['showImage'] !== false;
  }

  getShowExcerpt(): boolean {
    return this.component.data['showExcerpt'] !== false;
  }

  getExcerptLength(): number {
    return this.component.data['excerptLength'] || 150;
  }

  getShowCategory(): boolean {
    return this.component.data['showCategory'] || false;
  }

  getShowReadMore(): boolean {
    return this.component.data['showReadMore'] !== false;
  }

  getReadMoreText(): string {
    return this.component.data['readMoreText'] || 'Lees meer';
  }

  getDateFormat(): string {
    return this.component.data['dateFormat'] || 'medium';
  }

  getImageHeight(): number {
    return this.component.data['imageHeight'] || 200;
  }

  getImagePosition(): string {
    // 'top' | 'left' | 'right' | 'background'
    return this.component.data['imagePosition'] || 'top';
  }

  getCardStyle(): string {
    // 'elevated' | 'bordered' | 'flat'
    return this.component.data['cardStyle'] || 'elevated';
  }

  // Styling getters (shared pattern)
  getBackgroundType(): string {
    return this.component.data['backgroundType'] || 'solid';
  }

  getBackgroundColor(): string {
    return this.component.data['backgroundColor'] || '#ffffff';
  }

  getGradientFrom(): string {
    return this.component.data['gradientFrom'] || '#ffffff';
  }

  getGradientTo(): string {
    return this.component.data['gradientTo'] || '#ffffff';
  }

  getBackgroundImage(): string {
    return this.component.data['backgroundImage'] || '';
  }

  getTitlePosition(): string {
    return this.component.data['titlePosition'] || 'left';
  }

  getHeadingColor(): string {
    return this.component.data['headingColor'] || '#111827';
  }

  getTextColor(): string {
    return this.component.data['textColor'] || '#374151';
  }

  getParallax(): boolean {
    return this.component.data['parallax'] || false;
  }

  getSectionHeight(): string {
    return this.component.data['sectionHeight'] || '';
  }

  getHeightUnit(): string {
    return this.component.data['heightUnit'] || 'px';
  }

  getPreviewStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    const backgroundType = this.getBackgroundType();

    if (backgroundType === 'solid') {
      styles['background-color'] = this.getBackgroundColor();
    } else if (backgroundType === 'gradient') {
      styles['background'] = `linear-gradient(to bottom, ${this.getGradientFrom()}, ${this.getGradientTo()})`;
    } else if (backgroundType === 'image') {
      const bgImage = this.getBackgroundImage();
      if (bgImage) {
        styles['background-image'] = `url(${bgImage})`;
        styles['background-size'] = 'cover';
        styles['background-position'] = 'center';
        if (this.getParallax()) {
          styles['background-attachment'] = 'fixed';
        }
      }
    }

    const sectionHeight = this.getSectionHeight();
    if (sectionHeight) {
      const unit = this.getHeightUnit();
      styles['min-height'] = `${sectionHeight}${unit}`;
    }

    return styles;
  }

  getTitlePositionClass(): string {
    const position = this.getTitlePosition();
    switch (position) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  }

  getGridColumnsClass(): string {
    const cols = this.getColumns();
    const gridClasses: { [key: number]: string } = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    };
    return gridClasses[cols] || gridClasses[3];
  }

  getCardClasses(): string {
    const style = this.getCardStyle();
    switch (style) {
      case 'elevated':
        return 'bg-white shadow-md hover:shadow-lg';
      case 'bordered':
        return 'bg-white border border-gray-200 hover:border-gray-300';
      case 'flat':
        return 'bg-gray-50 hover:bg-gray-100';
      default:
        return 'bg-white shadow-md hover:shadow-lg';
    }
  }

  hasPosts(): boolean {
    return this.posts && this.posts.length > 0;
  }

  getPostTitle(post: NewsPost): string {
    return post.title || 'Untitled';
  }

  getPostExcerpt(post: NewsPost): string {
    const content = post.content || '';
    // Strip HTML tags
    const stripped = content.replace(/<[^>]*>/g, '');
    const length = this.getExcerptLength();
    if (stripped.length > length) {
      return stripped.substring(0, length) + '...';
    }
    return stripped;
  }

  getPostImage(post: NewsPost): string {
    return post.featuredImage || '';
  }

  getPostDate(post: NewsPost): string {
    if (!post.createdAt) return '';
    const date = post.createdAt;
    const format = this.getDateFormat();

    switch (format) {
      case 'short':
        return date.toLocaleDateString(this.currentLocale === 'nl' ? 'nl-NL' : 'en-US', {
          day: 'numeric',
          month: 'numeric',
          year: '2-digit'
        });
      case 'medium':
        return date.toLocaleDateString(this.currentLocale === 'nl' ? 'nl-NL' : 'en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
      case 'long':
        return date.toLocaleDateString(this.currentLocale === 'nl' ? 'nl-NL' : 'en-US', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      case 'relative':
        return this.getRelativeTime(date);
      default:
        return date.toLocaleDateString();
    }
  }

  private getRelativeTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Vandaag';
    if (days === 1) return 'Gisteren';
    if (days < 7) return `${days} dagen geleden`;
    if (days < 30) return `${Math.floor(days / 7)} weken geleden`;
    if (days < 365) return `${Math.floor(days / 30)} maanden geleden`;
    return `${Math.floor(days / 365)} jaar geleden`;
  }

  getPostLink(post: NewsPost): string {
    let prefix = 'posts';
    if (post.categoryIds?.length) {
      const cat = this.allCategories.find(c => c.id === post.categoryIds![0]);
      if (cat) {
        prefix = cat.slug || cat.name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
      }
    }
    return post.slug ? `/${prefix}/${post.slug}` : `/${prefix}/${post.id}`;
  }

  // For featured layout - get first post
  getFeaturedPost(): NewsPost | null {
    return this.posts.length > 0 ? this.posts[0] : null;
  }

  // For featured layout - get remaining posts
  getRemainingPosts(): NewsPost[] {
    return this.posts.slice(1);
  }

  // Get category name by ID
  getCategoryName(categoryId?: string): string {
    if (!categoryId) return 'Geen categorie';
    const category = this.categories.find(c => c.id === categoryId);
    return category?.name || 'Onbekend';
  }

  // Get all loaded categories
  getCategories(): NewsCategory[] {
    return this.categories;
  }
}
